/**
 * useCoinBalanceSync Hook
 * 
 * Subscribes to WebSocket coin balance changes and updates the coinStore.
 * Should be used at the app level to keep coin balance in sync.
 */

import { useEffect } from 'react';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType } from '../services/websocket/types';
import { useCoinStore } from '../stores/coinStore';

interface CoinBalanceChangedMessage {
  userId: string;
  balance: number;
  changeAmount: number;
  changeType: string;
  description?: string;
  timestamp: string;
}

/**
 * Hook to sync coin balance via WebSocket
 * 
 * This hook subscribes to COIN_BALANCE_CHANGED messages and updates
 * the coinStore balance in real-time.
 */
export function useCoinBalanceSync(): void {
  const { client } = useWebSocketContext();
  const updateBalance = useCoinStore((state) => state.updateBalance);

  useEffect(() => {
    if (!client) {
      return;
    }

    // Subscribe to coin balance changes
    const unsubscribe = client.onMessage(
      MessageType.COIN_BALANCE_CHANGED,
      (message: CoinBalanceChangedMessage) => {
        console.log('[CoinBalanceSync] 💰 Received balance update:', {
          balance: message.balance,
          changeAmount: message.changeAmount,
          changeType: message.changeType,
          description: message.description
        });

        // Update the store with new balance
        updateBalance(message.balance);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [client, updateBalance]);
}
